import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'

// Actions - used with useDispatch
export const branchSlice = createSlice({
  name: 'branch',
  initialState: {
    branches: null,
    currentSelectedBranchCode: null,
    getBranches: {
      status: null,
      error: null,
    },
    updateBranch: {
      status: null,
      error: null,
    },
    createBranch: {
      status: null,
      error: null,
    },
    setBranchActiveStatus: {
      status: null,
      error: null,
    },
    requestNewManager: {
      status: null,
      error: null,
    },
    assignNewManager: {
      status: null,
      error: null,
    },
    unassignManager: {
      status: null,
      error: null,
    },
    requestNewDriver: {
      status: null,
      error: null,
    },
    assignNewDriver: {
      status: null,
      error: null,
    },
    unassignDriver: {
      status: null,
      error: null,
    },
    nearestMarket: {
      status: null,
      error: null,
    },
    getAllDeliveryMethods: {
      status: null,
      error: null,
      data: null,
    },
    getAcceptedDeliveryMethods: {
      status: null,
      error: null,
      data: null,
    },
    acceptDeliveryMethod: {
      status: null,
      error: null,
    },
    removeDeliveryMethod: {
      status: null,
      error: null,
    },
    createOrUpdateBranchDeliveryMethodOverride: {
      status: null,
      error: null,
    },
    getBranchDeliveryMethodOverride: {
      status: null,
      error: null,
      data: null,
    },
    deleteBranchDeliveryMethodOverride: {
      status: null,
      error: null,
    },
    updateDeliveryMethodOrder: {
      status: null,
      error: null,
    },
  },
  reducers: {
    switchBranch: (state, action) => {
      state.currentSelectedBranchCode = action.payload
    },
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
    clearBranch: (state) => {
      // Clear state.branch.branches and state.branch.currentSelectedBranchCode
      state.branches = null
      state.currentSelectedBranchCode = null
    },
  },
  extraReducers: (builder) => {
    builder
      // getBranches
      .addCase(getBranches.pending, (state, action) => {
        state.getBranches.status = 'loading'
      })
      .addCase(getBranches.fulfilled, (state, action) => {
        state.getBranches.status = 'succeeded'
        state.getBranches.error = null // Clear any previous errors

        // Store branches info
        if (action.payload.data.success) {
          state.branches = action.payload.data.data
        }
      })
      .addCase(getBranches.rejected, (state, action) => {
        state.getBranches.status = 'failed'
        state.getBranches.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        state.branches = null
      })
      // updateBranch
      .addCase(updateBranch.pending, (state, action) => {
        state.updateBranch.status = 'loading'
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.updateBranch.status = 'succeeded'
        state.updateBranch.error = null // Clear any previous errors

        // Reflect updates
        if (action.payload.data.success) {
          const branchIndex = state.branches.findIndex(
            (branch) => branch.branchCode === state.currentSelectedBranchCode,
          )

          if (branchIndex !== -1) {
            // Create a new object with the updated data
            const updatedBranch = { ...state.branches[branchIndex], ...action.payload.data.data }

            // Create a new array with the updated object
            const updatedBranches = [...state.branches]
            updatedBranches[branchIndex] = updatedBranch

            // Update the state
            state.branches = updatedBranches
          }
        }
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.updateBranch.status = 'failed'
        state.updateBranch.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // createBranch
      .addCase(createBranch.pending, (state, action) => {
        state.createBranch.status = 'loading'
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.createBranch.status = 'succeeded'
        state.createBranch.error = null // Clear any previous errors

        // Reflect updates
        if (action.payload.data.success) {
          const branchIndex = state.branches.findIndex(
            (branch) => branch.branchCode === state.currentSelectedBranchCode,
          )

          if (branchIndex !== -1) {
            // Create a new object with the created data
            const createdBranch = { ...state.branches[branchIndex], ...action.payload.data.data }

            // Create a new array with the updated object
            const updatedBranches = [...state.branches]
            updatedBranches[branchIndex] = createdBranch

            // Update the state
            state.branches = updatedBranches
          }
        }
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.createBranch.status = 'failed'
        state.createBranch.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // setBranchActiveStatus
      .addCase(setBranchActiveStatus.pending, (state, action) => {
        state.setBranchActiveStatus.status = 'loading'
      })
      .addCase(setBranchActiveStatus.fulfilled, (state, action) => {
        state.setBranchActiveStatus.status = 'succeeded'
        state.setBranchActiveStatus.error = null // Clear any previous errors

        // Reflect updates
        if (action.payload.data.success) {
          const branchIndex = state.branches.findIndex(
            (branch) => branch.branchCode === action.payload.data.data.branchCode,
          )

          if (branchIndex !== -1) {
            // Create a new object with the updated data
            const updatedBranch = { ...state.branches[branchIndex], ...action.payload.data.data }

            // Create a new array with the updated object
            const updatedBranches = [...state.branches]
            updatedBranches[branchIndex] = updatedBranch

            // Update the state
            state.branches = updatedBranches
          }
        }
      })
      .addCase(setBranchActiveStatus.rejected, (state, action) => {
        state.setBranchActiveStatus.status = 'failed'
        state.setBranchActiveStatus.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // requestNewManager
      .addCase(requestNewManager.pending, (state, action) => {
        state.requestNewManager.status = 'loading'
      })
      .addCase(requestNewManager.fulfilled, (state, action) => {
        state.requestNewManager.status = 'succeeded'
        state.requestNewManager.error = null // Clear any previous errors
      })
      .addCase(requestNewManager.rejected, (state, action) => {
        state.requestNewManager.status = 'failed'
        state.requestNewManager.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // assignNewManager
      .addCase(assignNewManager.pending, (state, action) => {
        state.assignNewManager.status = 'loading'
      })
      .addCase(assignNewManager.fulfilled, (state, action) => {
        state.assignNewManager.status = 'succeeded'
        state.assignNewManager.error = null // Clear any previous errors
      })
      .addCase(assignNewManager.rejected, (state, action) => {
        state.assignNewManager.status = 'failed'
        state.assignNewManager.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // unassignManager
      .addCase(unassignManager.pending, (state, action) => {
        state.unassignManager.status = 'loading'
      })
      .addCase(unassignManager.fulfilled, (state, action) => {
        state.unassignManager.status = 'succeeded'
        state.unassignManager.error = null // Clear any previous errors
      })
      .addCase(unassignManager.rejected, (state, action) => {
        state.unassignManager.status = 'failed'
        state.unassignManager.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // requestNewDriver
      .addCase(requestNewDriver.pending, (state, action) => {
        state.requestNewDriver.status = 'loading'
      })
      .addCase(requestNewDriver.fulfilled, (state, action) => {
        state.requestNewDriver.status = 'succeeded'
        state.requestNewDriver.error = null
      })
      .addCase(requestNewDriver.rejected, (state, action) => {
        state.requestNewDriver.status = 'failed'
        state.requestNewDriver.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // assignNewDriver
      .addCase(assignNewDriver.pending, (state, action) => {
        state.assignNewDriver.status = 'loading'
      })
      .addCase(assignNewDriver.fulfilled, (state, action) => {
        state.assignNewDriver.status = 'succeeded'
        state.assignNewDriver.error = null
      })
      .addCase(assignNewDriver.rejected, (state, action) => {
        state.assignNewDriver.status = 'failed'
        state.assignNewDriver.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // unassignDriver
      .addCase(unassignDriver.pending, (state, action) => {
        state.unassignDriver.status = 'loading'
      })
      .addCase(unassignDriver.fulfilled, (state, action) => {
        state.unassignDriver.status = 'succeeded'
        state.unassignDriver.error = null
      })
      .addCase(unassignDriver.rejected, (state, action) => {
        state.unassignDriver.status = 'failed'
        state.unassignDriver.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // getNearestMarket
      .addCase(getNearestMarket.pending, (state) => {
        state.nearestMarket.status = 'loading'
      })
      .addCase(getNearestMarket.fulfilled, (state, action) => {
        state.nearestMarket.status = 'succeeded'
        state.nearestMarket.error = null
      })
      .addCase(getNearestMarket.rejected, (state, action) => {
        state.nearestMarket.status = 'failed'
        state.nearestMarket.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // getAllDeliveryMethods
      .addCase(getAllDeliveryMethods.pending, (state) => {
        state.getAllDeliveryMethods.status = 'loading'
      })
      .addCase(getAllDeliveryMethods.fulfilled, (state, action) => {
        state.getAllDeliveryMethods.status = 'succeeded'
        state.getAllDeliveryMethods.error = null
        state.getAllDeliveryMethods.data = action.payload.data.data
      })
      .addCase(getAllDeliveryMethods.rejected, (state, action) => {
        state.getAllDeliveryMethods.status = 'failed'
        state.getAllDeliveryMethods.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // getAcceptedDeliveryMethods
      .addCase(getAcceptedDeliveryMethods.pending, (state) => {
        state.getAcceptedDeliveryMethods.status = 'loading'
      })
      .addCase(getAcceptedDeliveryMethods.fulfilled, (state, action) => {
        state.getAcceptedDeliveryMethods.status = 'succeeded'
        state.getAcceptedDeliveryMethods.error = null
        state.getAcceptedDeliveryMethods.data = action.payload.data.data
      })
      .addCase(getAcceptedDeliveryMethods.rejected, (state, action) => {
        state.getAcceptedDeliveryMethods.status = 'failed'
        state.getAcceptedDeliveryMethods.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // acceptDeliveryMethod
      .addCase(acceptDeliveryMethod.pending, (state) => {
        state.acceptDeliveryMethod.status = 'loading'
      })
      .addCase(acceptDeliveryMethod.fulfilled, (state, action) => {
        state.acceptDeliveryMethod.status = 'succeeded'
        state.acceptDeliveryMethod.error = null

        // Add responsed delivery method to getAcceptedDeliveryMethods.data
        state.getAcceptedDeliveryMethods.data.push(action.payload.data.data)
      })
      .addCase(acceptDeliveryMethod.rejected, (state, action) => {
        state.acceptDeliveryMethod.status = 'failed'
        state.acceptDeliveryMethod.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // removeDeliveryMethod
      .addCase(removeDeliveryMethod.pending, (state) => {
        state.removeDeliveryMethod.status = 'loading'
      })
      .addCase(removeDeliveryMethod.fulfilled, (state, action) => {
        state.removeDeliveryMethod.status = 'succeeded'
        state.removeDeliveryMethod.error = null

        // Remove the removed delivery method from getAcceptedDeliveryMethods.data
        state.getAcceptedDeliveryMethods.data = state.getAcceptedDeliveryMethods.data.filter(
          (method) => method.deliveryMethodNumber !== action.payload.data.data.deliveryMethodNumber,
        )
      })
      .addCase(removeDeliveryMethod.rejected, (state, action) => {
        state.removeDeliveryMethod.status = 'failed'
        state.removeDeliveryMethod.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // createOrUpdateBranchDeliveryMethodOverride
      .addCase(createOrUpdateBranchDeliveryMethodOverride.pending, (state) => {
        state.createOrUpdateBranchDeliveryMethodOverride.status = 'loading'
      })
      .addCase(createOrUpdateBranchDeliveryMethodOverride.fulfilled, (state, action) => {
        state.createOrUpdateBranchDeliveryMethodOverride.status = 'succeeded'
        state.createOrUpdateBranchDeliveryMethodOverride.error = null
      })
      .addCase(createOrUpdateBranchDeliveryMethodOverride.rejected, (state, action) => {
        state.createOrUpdateBranchDeliveryMethodOverride.status = 'failed'
        state.createOrUpdateBranchDeliveryMethodOverride.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // getBranchDeliveryMethodOverride
      .addCase(getBranchDeliveryMethodOverride.pending, (state) => {
        state.getBranchDeliveryMethodOverride.status = 'loading'
      })
      .addCase(getBranchDeliveryMethodOverride.fulfilled, (state, action) => {
        state.getBranchDeliveryMethodOverride.status = 'succeeded'
        state.getBranchDeliveryMethodOverride.error = null
        state.getBranchDeliveryMethodOverride.data = action.payload.data.data
      })
      .addCase(getBranchDeliveryMethodOverride.rejected, (state, action) => {
        state.getBranchDeliveryMethodOverride.status = 'failed'
        state.getBranchDeliveryMethodOverride.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
        state.getBranchDeliveryMethodOverride.data = null
      })
      // deleteBranchDeliveryMethodOverride
      .addCase(deleteBranchDeliveryMethodOverride.pending, (state) => {
        state.deleteBranchDeliveryMethodOverride.status = 'loading'
      })
      .addCase(deleteBranchDeliveryMethodOverride.fulfilled, (state, action) => {
        state.deleteBranchDeliveryMethodOverride.status = 'succeeded'
        state.deleteBranchDeliveryMethodOverride.error = null
      })
      .addCase(deleteBranchDeliveryMethodOverride.rejected, (state, action) => {
        state.deleteBranchDeliveryMethodOverride.status = 'failed'
        state.deleteBranchDeliveryMethodOverride.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // updateDeliveryMethodOrder
      .addCase(updateDeliveryMethodOrder.pending, (state) => {
        state.updateDeliveryMethodOrder.status = 'loading'
      })
      .addCase(updateDeliveryMethodOrder.fulfilled, (state, action) => {
        state.updateDeliveryMethodOrder.status = 'succeeded'
        state.updateDeliveryMethodOrder.error = null
        // Refresh accepted delivery methods to get updated order
        // The order will be reflected when getAcceptedDeliveryMethods is called again
      })
      .addCase(updateDeliveryMethodOrder.rejected, (state, action) => {
        state.updateDeliveryMethodOrder.status = 'failed'
        state.updateDeliveryMethodOrder.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})
export const { switchBranch, clearStatus, clearBranch } = branchSlice.actions

// Async API (Thunks) - used with useDispatch
export const getBranches = createAsyncThunk('branch/branches', async (_, { rejectWithValue }) => {
  try {
    const params = { getMarketShopStatus: true }
    const response = await client.get('/shop/branches', { params })

    return response
  } catch (error) {
    // Handle the error gracefully and return it as part of the payload
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      return rejectWithValue({
        status: 503,
        message: error.message,
      })
    }
    // API response error
    return rejectWithValue({
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message,
    })
  }
})

export const updateBranch = createAsyncThunk(
  'branch/updateBranch',
  async (branch, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (branch) {
        // Transform Address to existing API style
        const shopName = state?.user?.profile?.shopName ?? ''
        const branchName =
          branch?.branchName ??
          state?.branch?.branches?.find(
            (b) => b.branchCode === state?.branch?.currentSelectedBranchCode,
          )?.branchName ??
          ''

        const addressName = shopName + (shopName && branchName ? ' ' : '') + branchName
        branch = {
          ...branch,
          addressName,
          ...(branch.branchAddress &&
            branch.branchAddress.contactName && { contactName: branch.branchAddress.contactName }),
          ...(branch.branchAddress &&
            branch.branchAddress.contactPhone && {
              contactPhone: branch.branchAddress.contactPhone,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.street && {
              street: branch.branchAddress.street,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.district && {
              district: branch.branchAddress.district,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.city && {
              city: branch.branchAddress.city,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.province && {
              province: branch.branchAddress.province,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.postalCode && {
              postalCode: branch.branchAddress.postalCode,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.province && {
              province: branch.branchAddress.province,
            }),
          ...(branch.branchAddress &&
            branch.branchAddress.location &&
            branch.branchAddress.location.coordinates && {
              latitude: branch.branchAddress.location.coordinates[1],
              longitude: branch.branchAddress.location.coordinates[0],
            }),
        }
        delete branch.branchAddress

        const response = await client.put('/shop/branches', branch)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const createBranch = createAsyncThunk(
  'branch/createBranch',
  async (branch, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (branch) {
        const marketCategory = 'Laundry'
        // Transform Address to existing API style
        const shopName = state?.user?.profile?.shopName ?? ''
        const branchName =
          branch?.branchName ??
          state?.branch?.branches?.find(
            (b) => b.branchCode === state?.branch?.currentSelectedBranchCode,
          )?.branchName ??
          ''

        const addressName = shopName + (shopName && branchName ? ' ' : '') + branchName
        branch = {
          ...branch,
          marketCategory,
          addressName,
          ...(branch?.branchAddress?.contactName && {
            contactName: branch.branchAddress.contactName,
          }),
          ...(branch?.branchAddress?.contactPhone && {
            contactPhone: branch.branchAddress.contactPhone,
          }),
          ...(branch?.branchAddress?.street && {
            street: branch.branchAddress.street,
          }),
          ...(branch?.branchAddress?.district && {
            district: branch.branchAddress.district,
          }),
          ...(branch?.branchAddress?.city && {
            city: branch.branchAddress.city,
          }),
          ...(branch?.branchAddress?.province && {
            province: branch.branchAddress.province,
          }),
          ...(branch?.branchAddress?.postalCode && {
            postalCode: branch.branchAddress.postalCode,
          }),
          ...(branch?.branchAddress?.province && {
            province: branch.branchAddress.province,
          }),
          ...(branch?.branchAddress?.location?.coordinates && {
            latitude: branch.branchAddress.location.coordinates[1],
            longitude: branch.branchAddress.location.coordinates[0],
          }),
        }
        delete branch.branchAddress

        const response = await client.post('/shop/branches', branch)

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Set branch active status
export const setBranchActiveStatus = createAsyncThunk(
  'branch/setBranchActiveStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await client.post(`/shop/branches/${payload.branchCode}/set-active`, payload)

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Request new manager
export const requestNewManager = createAsyncThunk(
  'branch/requestNewManager',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/managers/request`,
          payload,
        )

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Assign new manager
export const assignNewManager = createAsyncThunk(
  'branch/assignNewManager',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/managers/assign`,
          payload,
        )

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Unassign manager
export const unassignManager = createAsyncThunk(
  'branch/unassignManager',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/managers/unassign`,
          payload,
        )

        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// New async thunks for drivers
export const requestNewDriver = createAsyncThunk(
  'branch/requestNewDriver',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/drivers/request`,
          payload,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const assignNewDriver = createAsyncThunk(
  'branch/assignNewDriver',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/drivers/assign`,
          payload,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const unassignDriver = createAsyncThunk(
  'branch/unassignDriver',
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/drivers/unassign`,
          payload,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// New async thunk for getting the nearest market
export const getNearestMarket = createAsyncThunk(
  'branch/getNearestMarket',
  async ({ latitude, longitude }, { rejectWithValue }) => {
    try {
      let marketCategory = 'Laundry'
      const response = await client.get(
        `/shop/nearest-market?marketCategory=${marketCategory}&latitude=${latitude}&longitude=${longitude}`,
      )
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// New thunks for delivery methods
export const getAllDeliveryMethods = createAsyncThunk(
  'branch/getAllDeliveryMethods',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.get(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/all-delivery-methods`,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getAcceptedDeliveryMethods = createAsyncThunk(
  'branch/getAcceptedDeliveryMethods',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.get(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods`,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const acceptDeliveryMethod = createAsyncThunk(
  'branch/acceptDeliveryMethod',
  async (deliveryMethodNumber, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods/${deliveryMethodNumber}/accept`,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const removeDeliveryMethod = createAsyncThunk(
  'branch/removeDeliveryMethod',
  async (deliveryMethodNumber, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.delete(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods/${deliveryMethodNumber}/remove`,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const createOrUpdateBranchDeliveryMethodOverride = createAsyncThunk(
  'branch/createOrUpdateBranchDeliveryMethodOverride',
  async ({ deliveryMethodNumber, overrideData }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods/${deliveryMethodNumber}/override`,
          overrideData,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getBranchDeliveryMethodOverride = createAsyncThunk(
  'branch/getBranchDeliveryMethodOverride',
  async (deliveryMethodNumber, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.get(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods/${deliveryMethodNumber}/override`,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const deleteBranchDeliveryMethodOverride = createAsyncThunk(
  'branch/deleteBranchDeliveryMethodOverride',
  async (deliveryMethodNumber, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.delete(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods/${deliveryMethodNumber}/override`,
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const updateDeliveryMethodOrder = createAsyncThunk(
  'branch/updateDeliveryMethodOrder',
  async (deliveryMethodOrders, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch.currentSelectedBranchCode) {
        const response = await client.put(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/delivery-methods/order`,
          { deliveryMethodOrders },
        )
        return response
      } else {
        return rejectWithValue('No branch is selected')
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Selector (Read data) - used with useSelector
export const selectBranch = (state) => {
  if (Array.isArray(state.branch.branches)) {
    return state.branch.branches.find(
      (branch) => branch.branchCode == state.branch.currentSelectedBranchCode,
    )
  }
}
export const selectBranches = (state) => state.branch.branches
export const selectGetBranchesStatus = (state) => state.branch.getBranches.status
export const selectGetBranchesError = (state) => state.branch.getBranches.error
export const selectUpdateBranchStatus = (state) => state.branch.updateBranch.status
export const selectUpdateBranchError = (state) => state.branch.updateBranch.error
export const selectCreateBranchStatus = (state) => state.branch.createBranch.status
export const selectCreateBranchError = (state) => state.branch.createBranch.error
export const selectSetBranchActiveStatusStatus = (state) =>
  state.branch.setBranchActiveStatus.status
export const selectSetBranchActiveStatusError = (state) => state.branch.setBranchActiveStatus.error
export const selectRequestNewManagerStatus = (state) => state.branch.requestNewManager.status
export const selectRequestNewManagerError = (state) => state.branch.requestNewManager.error
export const selectAssignNewManagerStatus = (state) => state.branch.assignNewManager.status
export const selectAssignNewManagerError = (state) => state.branch.assignNewManager.error
export const selectUnassignManagerStatus = (state) => state.branch.unassignManager.status
export const selectUnassignManagerError = (state) => state.branch.unassignManager.error
export const selectRequestNewDriverStatus = (state) => state.branch.requestNewDriver.status
export const selectRequestNewDriverError = (state) => state.branch.requestNewDriver.error
export const selectAssignNewDriverStatus = (state) => state.branch.assignNewDriver.status
export const selectAssignNewDriverError = (state) => state.branch.assignNewDriver.error
export const selectUnassignDriverStatus = (state) => state.branch.unassignDriver.status
export const selectUnassignDriverError = (state) => state.branch.unassignDriver.error
export const selectNearestMarketStatus = (state) => state.branch.nearestMarket.status
export const selectNearestMarketError = (state) => state.branch.nearestMarket.error
export const selectAllDeliveryMethods = (state) => state.branch.getAllDeliveryMethods.data
export const selectAllDeliveryMethodsStatus = (state) => state.branch.getAllDeliveryMethods.status
export const selectAllDeliveryMethodsError = (state) => state.branch.getAllDeliveryMethods.error
export const selectAcceptedDeliveryMethods = (state) => state.branch.getAcceptedDeliveryMethods.data
export const selectAcceptedDeliveryMethodsStatus = (state) =>
  state.branch.getAcceptedDeliveryMethods.status
export const selectAcceptedDeliveryMethodsError = (state) =>
  state.branch.getAcceptedDeliveryMethods.error
export const selectAcceptDeliveryMethodStatus = (state) => state.branch.acceptDeliveryMethod.status
export const selectAcceptDeliveryMethodError = (state) => state.branch.acceptDeliveryMethod.error
export const selectRemoveDeliveryMethodStatus = (state) => state.branch.removeDeliveryMethod.status
export const selectRemoveDeliveryMethodError = (state) => state.branch.removeDeliveryMethod.error
export const selectCreateOrUpdateBranchDeliveryMethodOverrideStatus = (state) =>
  state.branch.createOrUpdateBranchDeliveryMethodOverride.status
export const selectCreateOrUpdateBranchDeliveryMethodOverrideError = (state) =>
  state.branch.createOrUpdateBranchDeliveryMethodOverride.error
export const selectGetBranchDeliveryMethodOverrideStatus = (state) =>
  state.branch.getBranchDeliveryMethodOverride.status
export const selectGetBranchDeliveryMethodOverrideError = (state) =>
  state.branch.getBranchDeliveryMethodOverride.error
export const selectGetBranchDeliveryMethodOverrideData = (state) =>
  state.branch.getBranchDeliveryMethodOverride.data
export const selectDeleteBranchDeliveryMethodOverrideStatus = (state) =>
  state.branch.deleteBranchDeliveryMethodOverride.status
export const selectDeleteBranchDeliveryMethodOverrideError = (state) =>
  state.branch.deleteBranchDeliveryMethodOverride.error
export const selectUpdateDeliveryMethodOrderStatus = (state) =>
  state.branch.updateDeliveryMethodOrder.status
export const selectUpdateDeliveryMethodOrderError = (state) =>
  state.branch.updateDeliveryMethodOrder.error

export default branchSlice.reducer
